import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { BookingDTO } from "@shared/types";
import type { Channel } from "@shared/domain";
import { api, ApiError } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { toast } from "./feedback";
import { useConnection } from "./connection";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/queryKeys";

/*
 * The offline outbox. Every new booking goes through here, online or not.
 * Each entry carries an idempotency key minted on the phone, so however many
 * times it is retried — flaky signal, double tap, app reopened — the server
 * creates it once.
 */

export type CreateBookingPayload = {
  propertyId: string;
  roomId: string;
  checkIn: string;
  checkOut: string;
  mode: "hold" | "confirm" | "block";
  guestName?: string;
  guestPhone?: string;
  guestGender?: string;
  channel?: Channel;
  adults?: number;
  children?: number;
  /** Dorms: beds held, and which ones when staff picked them. */
  bedsCount?: number;
  bedIds?: string[];
  /** Private rooms: extra beds beyond what the room sleeps. */
  extraBeds?: number;
  mealPlanId?: string;
  addons?: { addonId?: string; label?: string; unit?: string; unitPrice?: number; quantity?: number; note?: string }[];
  /** One per head, adults before children, lead guest first. */
  occupants?: { kind: "adult" | "child"; name: string }[];
  pointOfContactName?: string;
  amount?: number;
  /** How the quote treated tax; the server refuses it if the property has switched since. */
  taxInclusive?: boolean;
  notes?: string;
  blockReason?: string;
};

export type OutboxItem = {
  key: string;
  payload: CreateBookingPayload;
  roomName: string;
  createdAt: number;
  status: "queued" | "sending" | "failed";
  error?: string;
};

export type SendResult =
  | { kind: "saved"; booking: BookingDTO }
  | { kind: "queued" }
  | { kind: "auth" }
  | { kind: "failed"; error: ApiError };

type OutboxState = {
  items: OutboxItem[];
  enqueue: (item: Omit<OutboxItem, "status" | "createdAt">) => void;
  send: (key: string) => Promise<SendResult>;
  flush: () => Promise<void>;
  discard: (key: string) => void;
};

let flushing = false;

export const useOutbox = create<OutboxState>()(
  persist(
    (set, get) => ({
      items: [],
      enqueue: (item) => set((s) => ({ items: [...s.items, { ...item, status: "queued", createdAt: Date.now() }] })),
      discard: (key) => set((s) => ({ items: s.items.filter((i) => i.key !== key) })),

      send: async (key) => {
        const item = get().items.find((i) => i.key === key);
        if (!item) return { kind: "failed", error: new ApiError(400, "gone", "That booking is no longer queued.") };
        set((s) => ({ items: s.items.map((i) => (i.key === key ? { ...i, status: "sending" } : i)) }));
        try {
          const booking = await api<BookingDTO>(endpoints.bookings.create, {
            method: "POST",
            body: item.payload,
            headers: { "Idempotency-Key": item.key },
          });
          set((s) => ({ items: s.items.filter((i) => i.key !== key) }));
          void queryClient.invalidateQueries();
          return { kind: "saved", booking };
        } catch (e) {
          const err = e instanceof ApiError ? e : new ApiError(500, "internal", String(e));
          if (err.isNetwork) {
            set((s) => ({ items: s.items.map((i) => (i.key === key ? { ...i, status: "queued" } : i)) }));
            return { kind: "queued" };
          }
          if (err.status === 401) {
            set((s) => ({ items: s.items.map((i) => (i.key === key ? { ...i, status: "queued" } : i)) }));
            return { kind: "auth" };
          }
          set((s) => ({ items: s.items.map((i) => (i.key === key ? { ...i, status: "failed", error: err.message } : i)) }));
          // A conflict can mean the property's pricing changed under the quote (taxes switched to inclusive): refetch it.
          if (err.status === 409) void queryClient.invalidateQueries({ queryKey: queryKeys.me.all });
          return { kind: "failed", error: err };
        }
      },

      flush: async () => {
        if (flushing) return;
        flushing = true;
        try {
          for (const item of get().items.filter((i) => i.status === "queued")) {
            const r = await get().send(item.key);
            if (r.kind === "saved") toast.saved(`${r.booking.code} synced · ${item.roomName}`);
            else if (r.kind === "failed") toast.error(`${item.roomName}: ${r.error.message}`);
            else break; // still offline, or signed out: try again later
          }
        } finally {
          flushing = false;
        }
      },
    }),
    {
      name: "kk-outbox",
      // Anything mid-flight when the tab closed is simply queued again.
      merge: (persisted, current) => ({
        ...current,
        items: ((persisted as { items?: OutboxItem[] })?.items ?? []).map((i) => (i.status === "sending" ? { ...i, status: "queued" } : i)),
      }),
    },
  ),
);

if (typeof window !== "undefined") {
  const tryFlush = () => {
    if (useOutbox.getState().items.some((i) => i.status === "queued")) void useOutbox.getState().flush();
  };
  window.addEventListener("online", tryFlush);
  useConnection.subscribe((s, prev) => {
    if (s.online && !prev.online) tryFlush();
  });
  setInterval(tryFlush, 20_000);
}

export const queuedCount = (items: OutboxItem[]) => items.filter((i) => i.status !== "failed").length;
